import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import type { TeacherCreateInput, TeacherUpdateInput } from '@bbl/validation';
import { api, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/people/Pagination';
import { StatusBadge } from '../components/people/StatusBadge';
import { type TeacherRecord } from '../components/people/types';
import {
  TeacherDrawer,
  type TeacherDrawerMode,
} from '../components/people/TeacherDrawer';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { timeAgo } from '../lib/format';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  teachersFilterConfig,
  TEACHERS_SORT_COLUMNS,
} from '../components/people/teachersFilterConfig';

const PAGE_SIZE = 20;
const teachersApi = api.crud<TeacherRecord, TeacherCreateInput, TeacherUpdateInput>('/api/teachers');

/**
 * URL search-param contract for the Teachers list. The toolkit is the single
 * source of truth for these values; the schema validates the raw URL at the
 * route boundary (everything optional, since a bare `/people/teachers` opens
 * on the configured defaults). Unknown values are coerced/stripped by the
 * toolkit's decoder, so this schema stays permissive on shape and strict only
 * on type.
 */
const teachersSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  sort: z.string().optional(),
  subject: z.string().optional(),
  confirmation: z.string().optional(),
  hasLogin: z.string().optional(),
  noActiveBatch: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_authed/people/teachers')({
  validateSearch: teachersSearchSchema,
  component: TeachersPage,
});

function TeachersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role === 'super_admin' || role === 'academic_admin';

  const [drawer, setDrawer] = useState<TeacherDrawerMode | null>(null);

  // The single source of truth for search / filters / sort / page, backed by
  // the route's URL search params. The adapter narrows the route's precisely-
  // typed search/navigate surface to the toolkit's structural `FilterRoute`
  // contract; the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigate = Route.useNavigate();
      return (opts) =>
        navigate({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(teachersFilterConfig, filterRoute, { pageSize: PAGE_SIZE });
  const { values, apiParams, setSort, setPage } = filters;

  const listQuery = useQuery({
    queryKey: ['teachers', apiParams] as const,
    queryFn: () => teachersApi.list(apiParams),
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ['teachers', 'detail', drawer?.kind === 'edit' ? drawer.id : null] as const,
    queryFn: () => {
      if (drawer?.kind !== 'edit') throw new Error('No teacher selected');
      return teachersApi.get(drawer.id);
    },
    enabled: drawer?.kind === 'edit',
  });

  const columns = useMemo<ColumnDef<TeacherRecord, unknown>[]>(() => {
    const cols: ColumnDef<TeacherRecord, unknown>[] = [
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Name"
            binding={TEACHERS_SORT_COLUMNS.name}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="font-medium text-[color:var(--color-text)]">
            {info.row.original.name}
          </span>
        ),
      },
      {
        id: 'employeeId',
        accessorKey: 'employeeId',
        enableSorting: false,
        header: 'Employee ID',
        cell: (info) => (
          <span className="font-mono text-xs text-[color:var(--color-text-muted)]">
            {info.row.original.employeeId}
          </span>
        ),
      },
      {
        id: 'email',
        accessorKey: 'email',
        enableSorting: false,
        header: 'Email',
        cell: (info) => (
          <span className="text-[color:var(--color-text-muted)]">{info.row.original.email}</span>
        ),
      },
      {
        id: 'contact',
        accessorKey: 'contact',
        enableSorting: false,
        header: 'Contact',
        cell: (info) => (
          <span className="text-[color:var(--color-text-muted)]">{info.row.original.contact}</span>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: false,
        header: 'Status',
        cell: (info) => <StatusBadge status={info.row.original.status} />,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Added"
            binding={TEACHERS_SORT_COLUMNS.createdAt}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            {timeAgo(info.row.original.createdAt)}
          </span>
        ),
      },
    ];

    if (canManage) {
      cols.push({
        id: 'edit',
        enableSorting: false,
        header: () => <span className="sr-only">Edit</span>,
        cell: (info) => (
          <div className="flex justify-end">
            <EditIconButton
              label={`Edit ${info.row.original.name}`}
              onClick={() => setDrawer({ kind: 'edit', id: info.row.original.id })}
            />
          </div>
        ),
      });
    }

    return cols;
  }, [canManage, values.sort, setSort]);

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  return (
    <ListOrOutlet basePath="/people/teachers">
    <ListShell
      title="Teachers"
      description="Faculty roster, qualifications, and teaching scope."
      primaryAction={
        canManage ? (
          <Button onClick={() => setDrawer({ kind: 'create' })} icon={<Plus size={16} aria-hidden="true" />}>
            Add teacher
          </Button>
        ) : undefined
      }
    >
      <FilterBar config={teachersFilterConfig} filters={filters} resultCount={total} />

      {listQuery.isError ? (
        <ErrorState
          title="Could not load teachers"
          description={
            listQuery.error instanceof ApiError
              ? listQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : (
        <>
          <DataTable<TeacherRecord>
            data={items}
            columns={columns}
            isLoading={isLoadingList}
            getRowId={(row) => row.id}
            onRowClick={(row) =>
              void navigate({
                to: '/people/teachers/$teacherId',
                params: { teacherId: row.id },
              })
            }
            emptyState={
              <EmptyState
                title="No teachers match these filters"
                description="Try clearing a filter, or add your first faculty member to assign them to batches."
                action={
                  canManage ? (
                    <Button
                      onClick={() => setDrawer({ kind: 'create' })}
                      icon={<Plus size={16} aria-hidden="true" />}
                    >
                      Add teacher
                    </Button>
                  ) : undefined
                }
              />
            }
          />
          <Pagination page={values.page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      {canManage ? (
        <TeacherDrawer
          mode={drawer}
          onClose={() => setDrawer(null)}
          initial={detailQuery.data}
          isLoadingInitial={drawer?.kind === 'edit' && detailQuery.isLoading}
          loadError={detailQuery.error instanceof Error ? detailQuery.error : null}
          onPortalChange={() => {
            void queryClient.invalidateQueries({ queryKey: ['teachers'] });
          }}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['teachers'] });
            toast('Teacher saved.', { variant: 'success' });
            setDrawer(null);
          }}
          onArchive={async (id, name) => {
            const ok = await confirm({
              title: 'Archive teacher?',
              description: `${name} will be moved to archived status and their portal login will be disabled.`,
              confirmLabel: 'Archive',
              variant: 'danger',
            });
            if (!ok) return;
            try {
              await teachersApi.remove(id);
              void queryClient.invalidateQueries({ queryKey: ['teachers'] });
              toast('Teacher archived.', { variant: 'success' });
              setDrawer(null);
            } catch (err) {
              toast(err instanceof ApiError ? err.message : 'Could not archive.', { variant: 'error' });
            }
          }}
        />
      ) : null}
    </ListShell>
    </ListOrOutlet>
  );
}
